import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { Media } from '../../models';
import { ImagAnchor, ImageFormat, ImageMode, ImageScale } from '../../types';
import { Image } from '../../models';
import { ImageConfigService } from '../../services/image-config.service';

@Component({
  selector: 'fstr-image',
  imports: [NgOptimizedImage, NgClass],
  templateUrl: './image.component.html',
  styleUrl: './image.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageComponent {
  //private config = inject(ImageProcessorConfig);
  private config = inject(ImageConfigService);
  // https://angular.io/api/common/NgOptimizedImage#properties
  loading = input<'lazy' | 'eager' | 'auto'>('auto');
  priority = input<boolean>(false);
  loaderParams = input<{ [key: string]: any }>({});
  disableOptimizedSrcset = input<boolean>(false);
  fill = input<boolean>(false);
  showPlaceholderFallback = input<boolean>(true);

  image = input<Image>();
  media = input<Media>();
  alt = input<string>();
  ngSrcset = input<string>('550w, 800w, 1200w, 1600w, 1920w'); // beschikbare afbeelding formaten
  sizes = input<string>('100vw');

  // When both width and height are filled, this is used to calculate the aspect-ratio
  width = input<number>(0);
  height = input<number>(0);

  cssClass = input<string>('');

  // Image processing options
  mode = input<ImageMode>();
  format = input<ImageFormat>(this.config?.defaultFormat || 'webp');
  quality = input<number>(80);
  scale = input<ImageScale>();
  other = input<string>(); // add other nonstandard query params like trim.threshold=90
  anchor = input<ImagAnchor>(); // overrides the default anchor in image.anchor

  // Inherited from CSS: $grid-breakpoints
  // is used to generate size attribute with collspans
  cssBreakPoints = {
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1400,
  };

  colSpan = input<{
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    xxl?: number;
  }>();

  allLoaderParams = computed(() => {
    const loaderParams = this.loaderParams();
    return {
      width: this.width(),
      height: this.height(),
      anchor: this.anchor() || this.image()?.anchor,
      mode: this.mode(),
      format: this.format(),
      quality: this.quality(),
      scale: this.scale(),
      other: this.other(),
      ...loaderParams,
    };
  });

  constructor() {
    if (!this.config?.placeholderUrl) {
      console.error('No placeholderUrl URL defined');
    }
  }

  get placeholderUrl() {
    return `url('${this.config?.placeholderUrl}')`;
  }

  safeFileName$ = computed(() => {
    const imageUrl = this.image()?.url || this.media()?.absoluteUrl;
    if (imageUrl) {
      return imageUrl.replace(/ /g, '%20');
    }

    return;
  });

  // TEST bootstrap columns
  colSizes = computed(() => {
    const colSpan = this.colSpan();

    if (!colSpan || !Object.keys(colSpan).length) {
      return;
    }

    const sizeRules: string[] = [];

    // Sets for each container width an srcset (so for full width images)
    Object.keys(colSpan).forEach(key => {
      const size = colSpan[key as keyof typeof colSpan];
      const breakpoint = this.cssBreakPoints[key as keyof typeof this.cssBreakPoints];

      if (!size || !breakpoint) {
        return;
      }

      const width = (100 / 12) * size;

      sizeRules.push(`(min-width: ${breakpoint}px) ${width}vw`); // TODO calculate width with max width of container & vw?
    });

    return sizeRules.join(',').concat(', 100vw');
  });
}
